import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Card,
  Space,
  Modal,
  message,
  Typography,
  Row,
  Col,
  Image,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Resizable, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import { ReviewReportsService } from "../services/review-reports.service";
import type {
  VisitReportForReview,
  ReviewReportsQuery,
} from "../types/review-reports.types";
import { VisitReportState } from "../types/review-reports.types";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// 可调整列宽的表头组件
interface ResizableHeaderProps {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  [key: string]: any;
}

const ResizableHeader: React.FC<ResizableHeaderProps> = ({
  width,
  onResize,
  children,
  ...restProps
}) => {
  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={(_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        onResize(size.width);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps}>{children}</th>
    </Resizable>
  );
};

const DEFAULT_COLUMN_WIDTHS = {
  id: 100,
  staffName: 100,
  customerName: 100,
  customerPhone: 140,
  elderInfo: 250,
  elderPhone: 140,
  elderAddress: 200,
  startAt: 150,
  endAt: 150,
  durationMinutes: 120,
  chatRecordUrl: 150,
  createdAt: 150,
  reviewedAt: 150,
  action: 120,
};

// 性别转换函数
const formatGender = (gender: "男" | "女" | "未知"): string => {
  return gender;
};

interface SearchParams {
  dateRange?: [Dayjs, Dayjs];
  reviewDateRange?: [Dayjs, Dayjs];
  customerName?: string;
  elderName?: string;
  staffName?: string;
}

const PublishedReports: React.FC = () => {
  const [form] = Form.useForm<SearchParams>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<VisitReportForReview[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<SearchParams>({});

  const [viewModalVisible, setViewModalVisible] = useState<boolean>(false);
  const [republishModalVisible, setRepublishModalVisible] =
    useState<boolean>(false);
  const [selectedReport, setSelectedReport] =
    useState<VisitReportForReview | null>(null);

  // 列宽度状态管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    DEFAULT_COLUMN_WIDTHS
  );

  // 处理列宽度调整
  const handleResize = (columnKey: string) => (width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: width,
    }));
  };

  // 初始化数据
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 构建查询参数的私有函数
  const buildQueryParams = (
    searchValues: SearchParams = {},
    page?: number,
    limit?: number
  ): ReviewReportsQuery => {
    const queryParams: ReviewReportsQuery = {
      page: page || pagination.current || 1,
      limit: limit || pagination.pageSize || 20,
      customerName: searchValues.customerName,
      elderName: searchValues.elderName,
      staffName: searchValues.staffName,
      state: VisitReportState.APPROVED, // 固定查询已审核报告
    };

    // 处理随访日期范围
    if (searchValues.dateRange && searchValues.dateRange.length === 2) {
      queryParams.startDate = searchValues.dateRange[0].format("YYYY-MM-DD");
      queryParams.endDate = searchValues.dateRange[1].format("YYYY-MM-DD");
    }

    // 处理审核日期范围
    if (
      searchValues.reviewDateRange &&
      searchValues.reviewDateRange.length === 2
    ) {
      queryParams.reviewedStartDate =
        searchValues.reviewDateRange[0].format("YYYY-MM-DD");
      queryParams.reviewedEndDate =
        searchValues.reviewDateRange[1].format("YYYY-MM-DD");
    }

    return queryParams;
  };

  // 搜索功能
  const handleSearch = async (values: SearchParams = {}) => {
    setLoading(true);
    setSearchParams(values);

    try {
      // 构建查询参数
      const queryParams = buildQueryParams(values);

      // 调用API
      const response = await ReviewReportsService.getReviewReports(queryParams);

      if (response.success) {
        setDataSource(response.data.items);
        setPagination((prev) => ({
          ...prev,
          total: response.data.total,
          current: response.data.page,
          pageSize: response.data.limit,
        }));
      } else {
        message.error("获取数据失败");
      }
    } catch (error) {
      console.error("搜索失败:", error);
      message.error("搜索失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    handleSearch();
  };

  // 分页变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
    // 重新搜索当前页数据
    const queryParams = buildQueryParams(
      searchParams,
      newPagination.current || 1,
      newPagination.pageSize || 20
    );

    ReviewReportsService.getReviewReports(queryParams)
      .then((response) => {
        if (response.success) {
          setDataSource(response.data.items);
          setPagination({
            ...newPagination,
            total: response.data.total,
          });
        }
      })
      .catch((error) => {
        console.error("分页查询失败:", error);
        message.error("获取数据失败");
      });
  };

  // 显示查看报告模态框
  const showViewModal = (record: VisitReportForReview) => {
    setSelectedReport(record);
    setViewModalVisible(true);
  };

  // 显示再次发布模态框
  const showRepublishModal = (record: VisitReportForReview) => {
    setSelectedReport(record);
    setRepublishModalVisible(true);
  };

  // 再次发布
  const handleRepublish = () => {
    message.success("报告已再次发布");
    setRepublishModalVisible(false);
  };

  const columns: ColumnsType<VisitReportForReview> = [
    {
      title: "报告ID",
      dataIndex: "id",
      key: "id",
      width: columnWidths.id,
      onHeaderCell: () => ({
        width: columnWidths.id,
        onResize: handleResize("id"),
      }),
    },
    {
      title: "服务人员",
      dataIndex: "staffName",
      key: "staffName",
      width: columnWidths.staffName,
      onHeaderCell: () => ({
        width: columnWidths.staffName,
        onResize: handleResize("staffName"),
      }),
    },
    {
      title: "客户姓名",
      dataIndex: "customerName",
      key: "customerName",
      width: columnWidths.customerName,
      onHeaderCell: () => ({
        width: columnWidths.customerName,
        onResize: handleResize("customerName"),
      }),
    },
    {
      title: "客户手机号",
      dataIndex: "customerPhone",
      key: "customerPhone",
      width: columnWidths.customerPhone,
      onHeaderCell: () => ({
        width: columnWidths.customerPhone,
        onResize: handleResize("customerPhone"),
      }),
    },
    {
      title: "老人信息",
      key: "elderInfo",
      width: columnWidths.elderInfo,
      onHeaderCell: () => ({
        width: columnWidths.elderInfo,
        onResize: handleResize("elderInfo"),
      }),
      render: (_, record) => {
        return record.elderInfo.map((elder, index) => (
          <div
            key={index}
            style={{
              marginBottom: index < record.elderInfo.length - 1 ? "8px" : "0",
            }}
          >
            {`${elder.name}，${formatGender(elder.gender)}，${
              elder.age ? `${elder.age}岁` : "年龄未知"
            }，${elder.identityName}`}
          </div>
        ));
      },
    },
    {
      title: "老人手机号",
      dataIndex: "elderPhone",
      key: "elderPhone",
      width: columnWidths.elderPhone,
      onHeaderCell: () => ({
        width: columnWidths.elderPhone,
        onResize: handleResize("elderPhone"),
      }),
    },
    {
      title: "老人住址",
      dataIndex: "elderAddress",
      key: "elderAddress",
      width: columnWidths.elderAddress,
      ellipsis: true,
      onHeaderCell: () => ({
        width: columnWidths.elderAddress,
        onResize: handleResize("elderAddress"),
      }),
    },
    {
      title: "随访开始时间",
      dataIndex: "startAt",
      key: "startAt",
      width: columnWidths.startAt,
      onHeaderCell: () => ({
        width: columnWidths.startAt,
        onResize: handleResize("startAt"),
      }),
      render: (time?: string) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "随访结束时间",
      dataIndex: "endAt",
      key: "endAt",
      width: columnWidths.endAt,
      onHeaderCell: () => ({
        width: columnWidths.endAt,
        onResize: handleResize("endAt"),
      }),
      render: (time?: string) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "随访时长(分钟)",
      dataIndex: "durationMinutes",
      key: "durationMinutes",
      width: columnWidths.durationMinutes,
      onHeaderCell: () => ({
        width: columnWidths.durationMinutes,
        onResize: handleResize("durationMinutes"),
      }),
      render: (duration?: number) => (duration ? `${duration}分钟` : "-"),
    },
    {
      title: "聊天记录",
      key: "chatRecordUrl",
      width: columnWidths.chatRecordUrl,
      onHeaderCell: () => ({
        width: columnWidths.chatRecordUrl,
        onResize: handleResize("chatRecordUrl"),
      }),
      render: (_, record) =>
        record.chatRecordUrl ? (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(record.chatRecordUrl, "_blank")}
          >
            查看聊天
          </Button>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "生成时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: columnWidths.createdAt,
      onHeaderCell: () => ({
        width: columnWidths.createdAt,
        onResize: handleResize("createdAt"),
      }),
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "审核时间",
      dataIndex: "reviewedAt",
      key: "reviewedAt",
      width: columnWidths.reviewedAt,
      onHeaderCell: () => ({
        width: columnWidths.reviewedAt,
        onResize: handleResize("reviewedAt"),
      }),
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "操作",
      key: "action",
      width: columnWidths.action,
      fixed: "right",
      onHeaderCell: () => ({
        width: columnWidths.action,
        onResize: handleResize("action"),
      }),
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Button
            type="primary"
            size="small"
            onClick={() => showViewModal(record)}
            block
          >
            查看报告
          </Button>
          <Button
            size="small"
            icon={<SendOutlined />}
            onClick={() => showRepublishModal(record)}
            block
          >
            再次发布
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Row gutter={16} style={{ width: "100%" }}>
            <Col span={8}>
              <Form.Item name="dateRange" label="随访日期范围">
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="reviewDateRange" label="审核日期范围">
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="customerName" label="客户姓名">
                <Input placeholder="请输入客户姓名" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: "100%", marginTop: 16 }}>
            <Col span={8}>
              <Form.Item name="elderName" label="老人姓名">
                <Input placeholder="请输入老人姓名" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="staffName" label="服务人员">
                <Input placeholder="请输入服务人员姓名" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={loading}
                >
                  搜索
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 列表区域 */}
      <Card>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 2000 }}
          components={{
            header: {
              cell: ResizableHeader,
            },
          }}
        />
      </Card>

      {/* 查看报告模态框 */}
      <Modal
        title={`查看报告【ID:${selectedReport?.id || "未知"}】`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        {selectedReport && (
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {/* 客户概要 */}
            <Card title="客户概要" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>客户姓名：</Text>
                  {selectedReport.customerName}
                </Col>
                <Col span={12}>
                  <Text strong>客户手机号：</Text>
                  {selectedReport.customerPhone}
                </Col>
              </Row>
            </Card>

            {/* 老人概要 */}
            <Card title="老人概要" size="small" style={{ marginBottom: 16 }}>
              {selectedReport.elderInfo.map((elder, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom:
                      index < selectedReport.elderInfo.length - 1
                        ? "16px"
                        : "0",
                  }}
                >
                  <Row gutter={16}>
                    <Col span={6}>
                      <Text strong>老人姓名：</Text>
                      {elder.name}
                    </Col>
                    <Col span={6}>
                      <Text strong>性别：</Text>
                      {elder.gender}
                    </Col>
                    <Col span={6}>
                      <Text strong>年龄：</Text>
                      {elder.age ? `${elder.age}岁` : "年龄未知"}
                    </Col>
                    <Col span={6}>
                      <Text strong>关系：</Text>
                      {elder.identityName}
                    </Col>
                  </Row>
                  {index < selectedReport.elderInfo.length - 1 && (
                    <div
                      style={{
                        height: "8px",
                        borderBottom: "1px solid #f0f0f0",
                        margin: "8px 0",
                      }}
                    />
                  )}
                </div>
              ))}
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Text strong>联系手机：</Text>
                  {selectedReport.elderPhone}
                </Col>
                <Col span={12}>
                  <Text strong>住址：</Text>
                  {selectedReport.elderAddress}
                </Col>
              </Row>
            </Card>

            {/* 随访时间 */}
            <Card title="随访时间" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Text strong>开始时间：</Text>
                  {selectedReport.startAt
                    ? dayjs(selectedReport.startAt).format("YYYY-MM-DD HH:mm")
                    : "-"}
                </Col>
                <Col span={8}>
                  <Text strong>结束时间：</Text>
                  {selectedReport.endAt
                    ? dayjs(selectedReport.endAt).format("YYYY-MM-DD HH:mm")
                    : "-"}
                </Col>
                <Col span={8}>
                  <Text strong>时长：</Text>
                  {selectedReport.durationMinutes
                    ? `${selectedReport.durationMinutes}分钟`
                    : "-"}
                </Col>
              </Row>
            </Card>

            {/* 服务人员概要 */}
            <Card
              title="服务人员概要"
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Text strong>姓名：</Text>
                  {selectedReport.staffName}
                </Col>
                <Col span={8}>
                  <Text strong>性别：</Text>
                  {selectedReport.staffGender}
                </Col>
                <Col span={8}>
                  <Text strong>年龄：</Text>
                  {selectedReport.staffAge
                    ? `${selectedReport.staffAge}岁`
                    : "-"}
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text strong>联系手机：</Text>
                  {selectedReport.staffPhone}
                </Col>
                <Col span={12}>
                  <Text strong>家庭住址：</Text>
                  {selectedReport.staffAddress}
                </Col>
              </Row>
            </Card>

            {/* 总体结论 */}
            <Card title="总体结论" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <Text strong>状态：</Text>
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                      color:
                        (selectedReport.overallConclusion?.status || 3) === 1
                          ? "#52c41a"
                          : (selectedReport.overallConclusion?.status || 3) ===
                            2
                          ? "#faad14"
                          : "#f5222d",
                      backgroundColor:
                        (selectedReport.overallConclusion?.status || 3) === 1
                          ? "#f6ffed"
                          : (selectedReport.overallConclusion?.status || 3) ===
                            2
                          ? "#fffbe6"
                          : "#fff2f0",
                    }}
                  >
                    {(selectedReport.overallConclusion?.status || 3) === 1
                      ? "一切安好"
                      : (selectedReport.overallConclusion?.status || 3) === 2
                      ? "建议关注"
                      : "危险警告"}
                  </span>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <Text strong>结论描述：</Text>
                  <div
                    style={{
                      marginTop: 8,
                      padding: 8,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 4,
                    }}
                  >
                    {selectedReport.overallConclusion?.comment || "暂无"}
                  </div>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Text strong>风险：</Text>
                  <div
                    style={{
                      marginTop: 8,
                      padding: 8,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 4,
                    }}
                  >
                    {Array.isArray(selectedReport.overallConclusion?.risks)
                      ? selectedReport.overallConclusion?.risks.join(", ")
                      : selectedReport.overallConclusion?.risks || "暂无风险"}
                  </div>
                </Col>
              </Row>
            </Card>

            {/* AI总结 */}
            <Card title="AI总结" size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>内容概要：</Text>
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 4,
                  }}
                >
                  {selectedReport.aiSummary?.contentSummary}
                </div>
              </div>
              <div>
                <Text strong>行动建议：</Text>
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 4,
                  }}
                >
                  {selectedReport.aiSummary?.actionAdvice}
                </div>
              </div>
            </Card>

            {/* 状态评估 */}
            <Card title="状态评估" size="small" style={{ marginBottom: 16 }}>
              {selectedReport?.conclusions &&
              selectedReport.conclusions.length > 0 ? (
                <div>
                  {selectedReport.conclusions.map((record, index) => (
                    <Card
                      key={index}
                      size="small"
                      style={{ marginBottom: 8 }}
                      title={`${record.item || `项目 ${index + 1}`}`}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>状态：</Text>
                        <span
                          style={{
                            marginLeft: 8,
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontWeight: "bold",
                            color:
                              (record.status || 3) === 1
                                ? "#52c41a"
                                : (record.status || 3) === 2
                                ? "#faad14"
                                : "#f5222d",
                            backgroundColor:
                              (record.status || 3) === 1
                                ? "#f6ffed"
                                : (record.status || 3) === 2
                                ? "#fffbe6"
                                : "#fff2f0",
                          }}
                        >
                          {(record.status || 3) === 1
                            ? "一切安好"
                            : (record.status || 3) === 2
                            ? "建议关注"
                            : "危险警告"}
                        </span>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>描述：</Text>
                        <div
                          style={{
                            marginTop: 8,
                            padding: 8,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 4,
                          }}
                        >
                          {record.content || "暂无"}
                        </div>
                      </div>
                      <div>
                        <Text strong>风险：</Text>
                        <div
                          style={{
                            marginTop: 8,
                            padding: 8,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 4,
                          }}
                        >
                          {Array.isArray(record.risks)
                            ? record.risks.join(", ")
                            : record.risks || "暂无风险"}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#999",
                    border: "1px dashed #d9d9d9",
                    borderRadius: "6px",
                  }}
                >
                  暂无状态评估记录
                </div>
              )}
            </Card>

            {/* 个性化需求 */}
            <Card title="个性化需求" size="small" style={{ marginBottom: 16 }}>
              <div
                style={{
                  padding: 8,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                }}
              >
                {selectedReport.requirement || "暂无"}
              </div>
            </Card>

            {/* 反馈信息 */}
            <Card title="反馈信息" size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>反馈评论：</Text>
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 4,
                  }}
                >
                  {selectedReport?.feedback?.comment || "暂无"}
                </div>
              </div>
              <div>
                <Text strong>反馈附件：</Text>
                {selectedReport?.feedback?.attachments &&
                selectedReport.feedback.attachments.length > 0 ? (
                  <div style={{ marginTop: 8 }}>
                    <Image.PreviewGroup>
                      {selectedReport.feedback.attachments.map(
                        (attachment, index) => (
                          <div
                            key={index}
                            style={{
                              display: "inline-block",
                              marginRight: 8,
                              marginBottom: 8,
                            }}
                          >
                            <Image
                              src={attachment}
                              alt={`反馈附件${index + 1}`}
                              style={{
                                maxWidth: "200px",
                                maxHeight: "200px",
                                borderRadius: "4px",
                                objectFit: "cover",
                              }}
                              preview={{
                                mask: "点击查看大图",
                              }}
                            />
                          </div>
                        )
                      )}
                    </Image.PreviewGroup>
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: 8,
                      padding: 8,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 4,
                      textAlign: "center",
                      color: "#999",
                    }}
                  >
                    暂无反馈附件
                  </div>
                )}
              </div>
            </Card>

            {/* 发布信息 */}
            <Card title="发布信息" size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Text strong>审核时间：</Text>
                  {dayjs(selectedReport.reviewedAt).format("YYYY-MM-DD HH:mm")}
                </Col>
                <Col span={8}>
                  <Text strong>状态：</Text>已发布
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Modal>

      {/* 再次发布模态框 */}
      <Modal
        title="再次发布报告"
        open={republishModalVisible}
        onOk={handleRepublish}
        onCancel={() => setRepublishModalVisible(false)}
        width={500}
      >
        {selectedReport && (
          <div>
            <p>
              <Text strong>报告ID：</Text>
              {selectedReport.id}
            </p>
            <p>
              <Text strong>客户姓名：</Text>
              {selectedReport.customerName}
            </p>
            <p>
              <Text strong>老人姓名：</Text>
              {selectedReport.elderInfo.map((elder) => elder.name).join("、")}
            </p>
            <p>
              <Text strong>服务人员：</Text>
              {selectedReport.staffName}
            </p>
            <p>
              <Text strong>随访时间：</Text>
              {selectedReport.startAt
                ? dayjs(selectedReport.startAt).format("YYYY-MM-DD HH:mm")
                : "时间未知"}
            </p>
            <p style={{ marginTop: 16, color: "#666" }}>
              确定要再次发布这份报告吗？再次发布后，相关人员将收到新的通知。
            </p>
          </div>
        )}
      </Modal>

      {/* 自定义样式 */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .react-resizable-handle {
            position: absolute;
            width: 10px;
            height: 100%;
            bottom: 0;
            right: -5px;
            cursor: col-resize;
            z-index: 1;
          }

          .react-resizable-handle:hover {
            border-right: 2px solid #1890ff;
          }

          .ant-table-thead > tr > th {
            position: relative;
          }

          .ant-table-thead > tr > th .react-resizable-handle {
            background: transparent;
          }

          .ant-table-thead > tr > th .react-resizable-handle:hover {
            background: rgba(24, 144, 255, 0.1);
          }
          .ant-form-item-label {
            min-width: 100px;
          }
        `,
        }}
      />
    </div>
  );
};

export default PublishedReports;
